'use client';

// ContentInputArea - Large multiline input for bot content creation

import { forwardRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Input } from '@boty/ui';
import { useTranslations } from 'next-intl';
import { useContentInput, type UseContentInputReturn } from './useContentInput';

export interface ContentInputAreaProps {
  /** Callback when content is submitted */
  onSubmit: (content: string) => void | Promise<void>;
  /** Whether the form is currently submitting */
  isSubmitting?: boolean;
  /** Optional ref to access hook internals */
  hookRef?: React.MutableRefObject<UseContentInputReturn | null>;
  /** Callback when form validity changes */
  onValidityChange?: (isValid: boolean) => void;
  /** Callback when content changes (for auto-save) */
  onContentChange?: (content: string) => void;
  /** Auto-focus the input on mount */
  autoFocus?: boolean;
  /** Label for submit button. When provided, a submit button is shown */
  submitLabel?: string;
  /** Inspiration hint — overrides placeholder with "Tell me about your [Bot Name]..." (Story 6.13) */
  inspirationHint?: string | null;
  /** Show Coral border flash on input (Story 6.13) */
  showCoralFlash?: boolean;
  /** Optional action element rendered on the left side of the character counter row */
  actionSlot?: React.ReactNode;
}

/**
 * ContentInputArea - A text area for users to input bot content
 *
 * Features:
 * - Responsive multiline input (3 rows mobile, 4 rows desktop)
 * - Character count display (X / 50,000)
 * - Placeholder: "Dump your content here..."
 * - Validation via Zod (max 50,000 chars)
 * - RTL support via CSS logical properties
 * - Auto-expands as user types
 *
 * @example
 * ```tsx
 * <ContentInputArea
 *   onSubmit={(content) => createBot(content)}
 *   isSubmitting={isPending}
 * />
 * ```
 */
export const ContentInputArea = forwardRef<HTMLDivElement, ContentInputAreaProps>(
  function ContentInputArea(
    { onSubmit, isSubmitting = false, hookRef, onValidityChange, onContentChange, autoFocus = false, submitLabel, inspirationHint, showCoralFlash, actionSlot },
    ref
  ) {
    const t = useTranslations('creator.contentInput');
    const hook = useContentInput();

    // Expose hook internals if ref provided
    if (hookRef) {
      // eslint-disable-next-line react-hooks/refs
      hookRef.current = hook;
    }

    const {
      content,
      characterCount,
      maxCharacters,
      isOverLimit,
      isValid,
      errorKey,
      register,
      handleSubmit,
    } = hook;

    // Notify parent of validity changes
    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    // Notify parent of content changes (for auto-save)
    useEffect(() => {
      onContentChange?.(content);
    }, [content, onContentChange]);

    const onFormSubmit = useCallback(
      async (data: { content: string }) => {
        await onSubmit(data.content);
      },
      [onSubmit]
    );

    // Determine error message for display
    const getErrorMessage = () => {
      if (!errorKey) return null;
      // errorKey is 'empty' or 'tooLong' from validation
      return t(`error.${errorKey}`);
    };

    const errorMessage = getErrorMessage();

    return (
      <Box
        ref={ref}
        component="form"
        onSubmit={handleSubmit(onFormSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
        }}
      >
        {/* Guided start for non-technical users (Story 19.54): a one-tap template
            skeleton + reassurance that messy paste is fine. Only when empty. */}
        {characterCount === 0 && !inspirationHint && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t('notSureWhatToAdd')}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => hook.setValue(t('template'))}
              disabled={isSubmitting}
              sx={{ textTransform: 'none', minWidth: 0, py: 0 }}
            >
              {t('templateButton')}
            </Button>
          </Box>
        )}
        <Input
          {...register('content')}
          multiline
          minRows={2}
          maxRows={6}
          placeholder={inspirationHint ? t('inspirationPlaceholder', { botName: inspirationHint }) : t('placeholder')}
          disabled={isSubmitting}
          error={!!errorMessage || isOverLimit}
          helperText={errorMessage}
          autoFocus={autoFocus}
          inputProps={{
            'aria-label': t('placeholder'),
            maxLength: maxCharacters + 1000, // Allow slight overage for UX feedback
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: { xs: '16px', md: '1rem' }, // 16px prevents iOS zoom on focus
              lineHeight: 1.6,
              ...(showCoralFlash && {
                borderColor: '#f97316',
                transition: 'border-color 150ms ease',
              }),
            },
            '& .MuiInputBase-input': {
              resize: 'vertical',
              minHeight: { xs: '36px', md: '48px' }, // ~2 rows mobile, ~2 rows desktop
            },
            ...(showCoralFlash && {
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#f97316',
                  borderWidth: 2,
                },
              },
            }),
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingInlineEnd: 1,
          }}
        >
          {submitLabel ? (
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isValid || isOverLimit}
              size="small"
            >
              {isSubmitting ? t('processing') : submitLabel}
            </Button>
          ) : actionSlot ? (
            actionSlot
          ) : (
            <Box />
          )}
          <Typography
            variant="caption"
            color={isOverLimit ? 'error' : 'text.secondary'}
            aria-live="polite"
          >
            {t('characterCount', { count: characterCount, max: maxCharacters })}
          </Typography>
        </Box>

        {/* Reassurance for non-technical users: messy paste is fine (Story 19.54) */}
        {characterCount === 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('pasteHint')}
          </Typography>
        )}
      </Box>
    );
  }
);
